/**
 * Android/iOS kabuğunun ince web köprüsü.
 *
 * V1 remote shell olduğu için web uygulaması sunucudan yüklenir; API ve oturum
 * aynı origin'de kalır. Burada yalnızca native kabuğun sağladığı yaşam döngüsü
 * olayları ele alınır. API anahtarı, oturum tokenı veya parola bu modüle girmez.
 */
import { App } from '@capacitor/app';
import { Capacitor } from '@capacitor/core';
import { StatusBar, Style } from '@capacitor/status-bar';
import { apiOrigin } from './apiBase.ts';
import { hashFromAppUrl } from './appLink.ts';

function openAppUrl(url: string): void {
  const hash = hashFromAppUrl(url, apiOrigin());
  if (hash !== null && window.location.hash !== hash) {
    window.location.hash = hash;
  }
}

/** Tam ekranda donanım geri tuşu önce tam ekrandan çıkar. */
function exitFullscreenIfActive(): boolean {
  const doc = document as Document & {
    webkitFullscreenElement?: Element | null;
    webkitExitFullscreen?: () => void;
  };
  if (doc.fullscreenElement !== null || (doc.webkitFullscreenElement ?? null) !== null) {
    if (typeof document.exitFullscreen === 'function') {
      void document.exitFullscreen().catch(() => undefined);
    } else {
      doc.webkitExitFullscreen?.();
    }
    return true;
  }

  // Tarayıcı fullscreen API'si yoksa useFullscreen CSS taklidine düşer.
  const cssStage = document.querySelector<HTMLElement>('.watch__stage.is-fullscreen');
  const cssExit = cssStage?.querySelector<HTMLButtonElement>('.watch__fullscreen');
  if (cssExit !== undefined && cssExit !== null) {
    cssExit.click();
    return true;
  }
  return false;
}

async function configureStatusBar(): Promise<void> {
  await StatusBar.setStyle({ style: Style.Light });
}

/**
 * Native kabukta bir kez çağrılır. Web'de no-op'tur; mevcut tarayıcı
 * davranışını ve testleri değiştirmez.
 */
export function initNativeShell(): void {
  if (!Capacitor.isNativePlatform()) return;

  void configureStatusBar().catch(() => undefined);

  // Uygulama kapalıyken gelen App Link'i soğuk başlangıçta yakala.
  void App.getLaunchUrl()
    .then((launch) => {
      if (launch?.url) openAppUrl(launch.url);
    })
    .catch(() => undefined);

  // Uygulama açıkken gelen App Link.
  void App.addListener('appUrlOpen', ({ url }) => openAppUrl(url));

  // Android geri tuşu: önce uygulama içi geçmiş, kökte ise arka plana al.
  void App.addListener('backButton', ({ canGoBack }) => {
    if (exitFullscreenIfActive()) return;
    if (canGoBack) {
      window.history.back();
      return;
    }
    void App.minimizeApp().catch(() => undefined);
  });
}
